import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Modal from "../components/popup/MultipMappingPopUp";
import { fetchInvoices } from "./Api";
import { formatDate } from "../utils";
import Loading from "../components/loading/Loading";
import ErrorMessage from "../components/error/Error";

const InvoiceTable = () => {
    const [activeTab, setActiveTab] = useState("milestone");
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedInvoices, setSelectedInvoices] = useState(null);
    const [selectedMilestones, setSelectedMilestones] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchInvoices(activeTab);
            setInvoices(response);
        } catch (err) {
            setError("Error Fetching data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    const handleInvoiceClick = (invoice) => {
        setSelectedInvoices(invoice);
    };

    const handleMilestoneClick = (invoice) => {
        setSelectedMilestones(invoice);
    };

    const handleCloseModal = () => {
        setSelectedInvoices(null);
        setSelectedMilestones(null);
    };

    return (
        <motion.div
            className="p-8 min-h-screen"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
        // style={{ background: "linear-gradient(135deg, #667eea, #764ba2)" }}
        >
            <div className="max-w-full mx-auto">
                <div className="flex mb-6 justify-center">
                    {["milestone", "masterInvoice"].map((tab) => (
                        <motion.button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-6 py-3 rounded-full mx-2 font-semibold transition-all duration-300 shadow-md 
            ${activeTab === tab
                                    ? "bg-gradient-to-r from-purple-500 to-purple-700 text-white shadow-lg"
                                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                }`}
                            whileHover={{ scale: 1.08 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            {tab === "milestone" ? "Milestone" : "Master Invoice"}
                        </motion.button>
                    ))}

                </div>

                <motion.div
                    className="bg-white rounded-2xl shadow-2xl overflow-hidden p-6"
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.4 }}
                >
                    {loading ? (
                        <Loading />
                    ) : error ? (
                        <ErrorMessage error={error} show={error} />
                    ) : (
                        <motion.table className="w-full border-collapse text-left" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
                            <thead>
                                <tr className="bg-gradient-to-r from-purple-600 to-blue-500 text-white">
                                    <th className="py-3 px-4">S.No</th>
                                    <th className="py-3 px-4">Master Invoice</th>
                                    <th className="py-3 px-4">Project Name</th>
                                    <th className="py-3 px-4">User</th>
                                    <th className="py-3 px-4">Deleted At</th>
                                    <th className="py-3 px-4 text-center">Invoices</th>
                                    <th className="py-3 px-4 text-center">Milestones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices?.map((invoice, index) => (
                                    <motion.tr
                                        key={index}
                                        className="hover:bg-gray-50 transition-all border-b"
                                        whileHover={{ scale: 1.02, boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)" }}
                                    >
                                        <td className="py-4 px-6 text-gray-700 font-medium">{index + 1}</td>
                                        <td className="py-4 px-6 text-blue-600 font-semibold">{invoice.masterInvoiceNumber}</td>
                                        <td className="py-4 px-6 text-blue-600 font-semibold">{invoice.name}</td>
                                        <td className="py-4 px-6 text-gray-600">{invoice.deletedBy}</td>
                                        <td className="py-4 px-6 text-gray-600">
                                            {formatDate(new Date(invoice.createdAt), "MMM dd, yyyy")}
                                        </td>
                                        <td className="py-4 px-6 text-center">
                                            <motion.button
                                                onClick={() => handleInvoiceClick(invoice)}
                                                className="px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-lg shadow-md hover:from-blue-600 hover:to-blue-800 transition-all font-semibold"
                                                whileHover={{ scale: 1.08 }}
                                                whileTap={{ scale: 0.95 }}
                                            >
                                                {invoice.invoiceNumbers?.length} Invoices
                                            </motion.button>
                                        </td>
                                        <td className="py-4 px-6 text-center">
                                            <motion.button
                                                onClick={() => handleMilestoneClick(invoice)}
                                                className="px-5 py-2 bg-gradient-to-r from-green-500 to-green-700 text-white rounded-lg shadow-md hover:from-green-600 hover:to-green-800 transition-all font-semibold"
                                                whileHover={{ scale: 1.08 }}
                                                whileTap={{ scale: 0.95 }}
                                            >
                                                {invoice.milestones?.length} Milestones
                                            </motion.button>
                                        </td>
                                    </motion.tr>

                                ))}
                            </tbody>
                        </motion.table>
                    )}
                </motion.div>
            </div>

            {selectedInvoices && (
                <Modal isOpen={!!selectedInvoices} onClose={handleCloseModal} title="Invoice Details">
                    <motion.div
                        className="p-6 bg-white rounded-lg shadow-lg"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                        <h3 className="text-xl font-bold text-gray-800 mb-3">
                            Master Invoice: <span className="text-blue-600">{selectedInvoices.masterInvoiceNumber}</span>
                        </h3>
                        <p className="text-gray-700"><strong>User:</strong> {selectedInvoices.deletedBy}</p>
                        <p className="text-gray-700"><strong>Invoices:</strong> {selectedInvoices?.invoiceNumbers?.join(", ")}</p>
                    </motion.div>
                </Modal>
            )}

            {selectedMilestones && (
                <Modal isOpen={!!selectedMilestones} onClose={handleCloseModal} title="Milestone Details">
                    <motion.div
                        className="p-6 bg-white rounded-lg shadow-lg"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                        <h3 className="text-xl font-bold text-gray-800 mb-3">
                            Master Invoice: <span className="text-blue-600">{selectedMilestones.masterInvoiceNumber}</span>
                        </h3>
                        <h4 className="mt-4 font-semibold text-lg text-gray-800">Milestones</h4>
                        <ul className="list-disc pl-5 space-y-2 mt-2">
                            {selectedMilestones.milestones?.map((milestone, index) => (
                                <li
                                    key={index}
                                    className="text-gray-700 bg-gray-100 px-3 py-2 rounded-md shadow-sm"
                                >
                                    <span className="font-medium">{milestone.name}</span> -
                                    <span className="text-blue-500 font-semibold"> {milestone.percentage}%</span>
                                    <span className={`ml-2 px-2 py-1 text-xs font-bold rounded ${milestone.status === "Completed"
                                            ? "bg-green-500 text-white"
                                            : "bg-yellow-500 text-white"
                                        }`}>
                                        {milestone.status}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                </Modal>
            )}

        </motion.div>
    );
};

export default InvoiceTable;